@tailwind base;
@tailwind components;
@tailwind utilities;

/* ─── Theme tokens ───────────────────────────────────────────────────────
   Channel triplets (space-separated RGB) so Tailwind opacity modifiers
   (e.g. bg-brand-navy/90) keep working. Default = dark (your current
   values). [data-theme="light"] overrides for the system-preference nicety.
   The accent (amber/amber2) is intentionally constant across both themes. */
:root {
    color-scheme: dark;
    --brand-navy: 13 17 23;
    /* #0D1117  page background */
    --brand-deep: 22 27 34;
    /* #161B22  inputs / recessed */
    --brand-surface: 28 35 51;
    /* #1C2333  cards */
    --brand-border: 48 54 61;
    /* #30363D */
    --brand-amber: 212 168 67;
    /* #D4A843  accent */
    --brand-amber2: 240 200 102;
    /* #F0C866  accent hover */
    --brand-muted: 139 148 158;
    /* #8B949E */
    --brand-text: 230 237 243;
    /* #E6EDF3 */
}

[data-theme="light"] {
    color-scheme: light;
    --brand-navy: 246 248 250;
    /* #F6F8FA  page background */
    --brand-deep: 255 255 255;
    /* #FFFFFF  inputs / recessed */
    --brand-surface: 255 255 255;
    /* #FFFFFF  cards (raised on off-white page) */
    --brand-border: 208 215 222;
    /* #D0D7DE */
    --brand-amber: 212 168 67;
    /* constant */
    --brand-amber2: 240 200 102;
    /* constant */
    --brand-muted: 87 96 106;
    /* #57606A */
    --brand-text: 31 35 40;
    /* #1F2328 */
}

@layer base {
    body {
        @apply bg-brand-navy text-brand-text font-body antialiased;
        transition: background-color .25s ease, color .25s ease;
    }

    h1,
    h2,
    h3 {
        @apply font-display;
    }
}

@layer components {
    .btn-primary {
        /* gold in BOTH themes → text must be a fixed dark ink.
       (brand-navy flips to light now, so it can't be the text colour here) */
        @apply bg-brand-amber text-[#0D1117] font-semibold px-6 py-3 rounded hover:bg-brand-amber2 transition-colors duration-200 font-body;
    }

    .btn-ghost {
        @apply border border-brand-border text-brand-text px-6 py-3 rounded hover:border-brand-amber hover:text-brand-amber transition-colors duration-200 font-body;
    }

    .card {
        @apply bg-brand-surface border border-brand-border rounded-lg p-6;
    }

    .input-field {
        @apply w-full bg-brand-deep border border-brand-border rounded px-4 py-3 text-brand-text placeholder-brand-muted focus:outline-none focus:border-brand-amber transition-colors duration-200 font-body text-sm;
    }

    .label {
        @apply block text-brand-muted text-xs font-medium uppercase tracking-widest mb-2 font-body;
    }

    /* ── Long-form document typography ──────────────────────────────────
       Applied to body copy on Terms, Privacy Policy, Zero Retention and
       Disclaimer so paragraph rhythm stays consistent across all four,
       rather than each page carrying its own spacing values. */
    .doc-body {
        @apply text-sm font-sans font-light text-slate-400 leading-relaxed;
    }

    .doc-body+.doc-body {
        @apply mt-5;
    }

    .doc-heading {
        @apply text-xl font-display font-semibold text-white tracking-tight;
    }

    .doc-section+.doc-section {
        @apply mt-14;
    }

    .doc-meta {
        @apply text-xs font-sans tracking-wide text-slate-500;
    }

    /* ── Inline mail links ──────────────────────────────────────────────
       Used by the linkify helper so every address renders identically
       whether it sits in JSX or inside a body string. */
    .mail-link {
        @apply text-brand-amber hover:text-emerald-400 underline decoration-brand-amber/30 hover:decoration-emerald-400/60 underline-offset-2 transition-colors duration-200 break-words;
    }

    /* ── Accent back-navigation ─────────────────────────────────────────
       "Return to Account Creation" and similar — reads as a deliberate
       control rather than incidental text. */
    /* Pair with the literal `group` class in markup:
       <Link className="back-link group"> ... </Link>
       `group` is a marker class and cannot be used with @apply. */
    .back-link {
        @apply inline-flex items-center gap-2 text-sm font-sans font-semibold text-brand-amber hover:text-emerald-400 transition-colors duration-200;
    }

    .back-link .back-arrow {
        @apply transition-transform duration-200;
    }

    .back-link:hover .back-arrow {
        transform: translateX(-0.25rem);
    }
}